<template>
  <div>
    <!-- Page header -->
    <CCard class="mb-4">
      <CCardBody class="p-4">
        <CButton color="link" class="px-0 mb-2" @click="cancel">
          <CIcon name="cil-arrow-left" class="mr-1" /> Rental products
        </CButton>
        <h1 class="h4 mb-1">
          {{ obj.id ? obj.name || "Rental product" : "Add rental product" }}
        </h1>
        <p class="text-muted mb-0">
          A room, unit or item you rent out. Its colour marks its bookings on the calendar.
        </p>
      </CCardBody>
    </CCard>

    <CCard>
      <div v-if="loading" class="text-center p-5">
        <CSpinner color="primary" />
      </div>
      <div v-else-if="error" class="text-center p-5">
        <CIcon name="cil-warning" size="xl" class="text-danger mb-2" />
        <h2 class="h6">Couldn't load this product</h2>
        <p class="text-muted">Check your connection and try again.</p>
        <CButton color="primary" variant="outline" @click="resetObj">
          <CIcon name="cil-reload" /> Retry
        </CButton>
      </div>
      <template v-else>
        <CCardBody>
          <CForm @submit.prevent="submit">
            <CInput
              label="Name *"
              v-model="obj.name"
              :is-valid="nameError ? false : null"
              invalid-feedback="Enter a name, such as “Deluxe room 101”"
            />
            <CInput label="Type (optional)" v-model="obj.type" placeholder="Room, chalet, car…" />
            <CTextarea
              label="Description (optional)"
              v-model="obj.description"
              placeholder=""
              rows="4"
            />

            <CRow form>
              <CCol sm="8" md="6">
                <CSelect
                  :value.sync="selectedColorName"
                  :options="presetColors.map((color) => ({ value: color.name, label: color.name }))"
                  label="Calendar colour"
                  @change="updateSelectedColor"
                />
              </CCol>
              <CCol sm="4" md="6" class="d-flex align-items-center">
                <!-- Preview of the stored calendar colour (data, not theme styling) -->
                <span
                  v-if="obj.colorCode"
                  class="d-inline-block border rounded p-3 mt-2"
                  :style="{ backgroundColor: obj.colorCode }"
                  role="img"
                  :aria-label="`Colour preview: ${obj.colorName || ''}`"
                ></span>
              </CCol>
            </CRow>
          </CForm>
        </CCardBody>
        <CCardFooter class="d-flex justify-content-between">
          <CButton color="light" :disabled="saving" @click="cancel">Cancel</CButton>
          <CButton color="primary" :disabled="saving" @click="submit">
            <CSpinner v-if="saving" size="sm" class="mr-1" />
            {{ obj.id ? "Save" : "Add product" }}
          </CButton>
        </CCardFooter>
      </template>
    </CCard>
  </div>
</template>

<script>
import RentalProductApi from "../../lib/rentalProductApi";

export default {
  name: "RentalProduct",
  components: {},
  data: () => {
    return {
      selectedColorName: "",
      // Calendar colours stored on the product (colorCode is saved to the backend and used
      // to paint its reservations), so these values are data rather than theme styling.
      presetColors: [
        { name: "Red", code: "#FF5733" },
        { name: "Blue", code: "#007bff" },
        { name: "Green", code: "#28a745" },
        { name: "Yellow", code: "#ffc107" },
        { name: "Purple", code: "#6f42c1" },
        { name: "Black", code: "#343a40" },
        { name: "White", code: "#f8f9fa" },
        { name: "Orange", code: "#fd7e14" },
        { name: "Brown", code: "#795548" },
        { name: "Cyan", code: "#17a2b8" },
        { name: "Amber", code: "#ffbf00" },
      ],
      obj: {},
      submitted: false,
      nameError: false,
      api: new RentalProductApi(),
      loading: false,
      error: false,
      saving: false,
    };
  },
  mounted() {
    this.resetObj();
  },

  methods: {
    updateSelectedColor() {
      const color = this.presetColors.find(
        (c) => c.name === this.selectedColorName
      );
      if (color) {
        this.selectedColorName = color.name;
        this.$set(this.obj, "colorName", color.name);
        this.$set(this.obj, "colorCode", color.code);
      }
    },

    resetObj() {
      var self = this;
      if (self.$route.params.id) {
        self.loading = true;
        self.error = false;
        self.api
          .get(self.$route.params.id)
          .then((response) => {
            self.obj = response.result;
            // Set default color if none exists
            if (!self.obj.colorCode) {
              self.obj.colorCode = "#007bff"; // Default Blue
              self.obj.colorName = "Blue";
            }
            self.selectedColorName = self.obj.colorName;
          })
          .catch((err) => {
            console.error(err);
            self.error = true;
          })
          .finally(() => {
            self.loading = false;
          });
      } else {
        self.obj = self.getEmptyObj();
      }
    },
    onSubmit() {
      var self = this;
      self.obj.date = self.issuedDate;
      if (self.selectedVendor) {
        self.obj.vendorId = self.selectedVendor.id;
      }

      self.saving = true;
      if (!self.obj.id) {
        this.api
          .create(self.obj)
          .then((response) => {
            self.obj = response.result;
            self.$toast.success("Rental product added");
            self.$router.push({
              path: `/tenants/RentalProduct/${self.obj.id}`,
            });
          })
          .catch(({ data }) => {
            self.$toast.error(
              `Couldn't add the product. ${helper.getErrorMessage(data)}`
            );
          })
          .finally(() => {
            self.saving = false;
          });
      } else {
        this.api
          .update(self.obj)
          .then(() => {
            self.$toast.success("Rental product saved");
          })
          .catch(({ data }) => {
            self.$toast.error(
              `Couldn't save the product. ${helper.getErrorMessage(data)}`
            );
          })
          .finally(() => {
            self.saving = false;
          });
      }
    },
    getEmptyObj() {
      return {
        name: "",
      };
    },
    submit() {
      if (this.saving) return;
      this.nameError = !this.obj.name || !this.obj.name.trim();
      if (this.nameError) return;
      this.onSubmit();
      this.submitted = true;
    },
    cancel() {
      this.$router.push({ path: "/tenants/RentalProductList" });
    },
  },
};
</script>
